import * as React from "react";
import { Plus, X } from "lucide-react";

import { AmountInput } from "@/components/admin/AmountInput";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/Combobox";
import { CURRENCIES } from "@/lib/countries";
import { cn } from "@/lib/utils";
import { AdvanceAmount } from "../../types";

const CURRENCY_OPTIONS = CURRENCIES.map((c) => ({ value: c, label: c }));

/** An Other reimbursement advance's amounts (City travel / Miscellaneous):
 *  any currency, no policy cap, each currency once, "Add currency" for
 *  another row. AdvanceAmountsField's travel twin is built around a fixed
 *  policy-currency first row and per-currency caps, neither of which applies
 *  here. Used by the Request advance sheet (OtherReimbursements.tsx) and an
 *  approver's Review (ReviewAdvanceDialog). The server checks the same rules
 *  (requests_api.py's _clean_other_amounts). */
export function OtherAdvanceAmountsField({
  value,
  onChange,
  idPrefix
}: {
  value: AdvanceAmount[];
  onChange: (value: AdvanceAmount[]) => void;
  idPrefix: string;
}) {
  const used = new Set(value.map((l) => l.currency));
  const row = "grid grid-cols-[7rem_minmax(0,1fr)_2.25rem] items-center gap-2";
  return (
    <div className="space-y-2">
      <div className={cn(row, "text-sm font-medium")}>
        <span>Currency</span>
        <span>Amount</span>
        <span className="sr-only">Remove</span>
      </div>
      {value.map((line, i) => (
        <div key={i} className={row}>
          <Combobox
            value={line.currency}
            onChange={(currency) => onChange(value.map((l, j) => (j === i ? { ...l, currency } : l)))}
            options={CURRENCY_OPTIONS.filter((o) => o.value === line.currency || !used.has(o.value))}
            placeholder="Currency"
            searchPlaceholder="Search…"
          />
          <AmountInput
            id={`${idPrefix}-amount-${i}`}
            value={line.amount}
            onChange={(amount) => onChange(value.map((l, j) => (j === i ? { ...l, amount } : l)))}
          />
          {value.length > 1 ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              aria-label={`Remove ${line.currency || "this row"}`}
              onClick={() => onChange(value.filter((_, j) => j !== i))}
            >
              <X className="h-4 w-4" />
            </Button>
          ) : (
            <span />
          )}
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange([...value, { currency: "", amount: 0 }])}
      >
        <Plus className="mr-1.5 h-3.5 w-3.5" />
        Add currency
      </Button>
    </div>
  );
}

/** The first problem with an Other advance's amounts, or null. */
export function otherAdvanceAmountsError(amounts: AdvanceAmount[]): string | null {
  if (amounts.length === 0) return "Enter an amount.";
  if (amounts.some((a) => !a.currency)) return "Select a currency for every amount.";
  const seen = new Set<string>();
  for (const a of amounts) {
    if (seen.has(a.currency)) return `${a.currency} is listed more than once.`;
    seen.add(a.currency);
    if (!(a.amount > 0)) return `Enter an amount greater than 0 for ${a.currency}.`;
  }
  return null;
}
